<template>
  <section
    v-if="variants.v1 === variant"
    class="n-section-secondary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <div class="tw-mb-10 tw-flex tw-flex-col tw-gap-3 tw-self-center tw-text-center lg:tw-mb-32">
      <small v-if="vars.altTitleText"> {{ vars.altTitleText }}</small>
      <h2>{{ vars.titleText }}</h2>
    </div>
    <MotionGroup :preset="vars.animationText ? vars.animationText : 'fadeVisible'" :duration="600" is="div">
      <div
        class="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-10 lg:tw-flex-row"
      >
        <div
          v-for="(value, index) in values"
          :key="index"
          class="value tw-group tw-flex tw-max-w-[600px] tw-flex-col tw-items-center tw-justify-start tw-gap-3 tw-rounded-[var(--rounded)] tw-text-center lg:tw-h-[460px] lg:tw-basis-1/3 lg:tw-items-start lg:tw-text-start xl:tw-h-[400px]"
        >
          <NuxtImg
            :src="value.icon"
            class="tw-mb-3 tw-w-24 tw-transition-all tw-duration-500 group-hover:tw-translate-y-1"
          />
          <div>
            <h6
              class="tw-line-clamp-2 tw-h-[55px] tw-transition-all tw-duration-500 group-hover:tw-text-[var(--c-primary)] md:tw-h-[65px]"
            >
              {{ value.title }}
            </h6>
            <p
              class="tw-mt-2 tw-max-h-[260px] tw-overflow-auto xl:tw-max-h-[205px]"
              :style="`color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'} !important`"
            >
              {{ value.description }}
            </p>
          </div>
        </div>
      </div>
    </MotionGroup>
  </section>

  <section
    v-if="variants.v2 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <div class="tw-mb-8 tw-flex tw-flex-col tw-gap-3 tw-self-center tw-text-center">
      <h2>{{ vars.titleText }}</h2>
    </div>
    <MotionGroup :preset="vars.animationText ? vars.animationText : 'fadeVisible'" :duration="600" is="div">
      <div
        class="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-20 lg:tw-flex-row"
      >
        <div
          v-for="(value, index) in values"
          :key="index"
          class="value tw-group tw-flex tw-max-w-[550px] tw-flex-col tw-items-start tw-justify-start tw-gap-3 tw-rounded-[var(--rounded)] tw-text-start lg:tw-h-[500px] lg:tw-basis-1/3 xl:tw-h-[460px]"
        >
          <NuxtImg
            :src="value.icon"
            class="tw-mb-5 tw-w-24 tw-transition-all tw-duration-500 group-hover:tw-translate-y-1"
          />
          <div>
            <h6
              class="tw-line-clamp-2 tw-h-[48px] tw-text-[var(--c-tertiary)] tw-transition-all tw-duration-500 group-hover:tw-text-[var(--c-primary)]"
            >
              {{ value.title }}
            </h6>
            <p class="tw-mt-2 tw-max-h-[260px] tw-overflow-auto xl:tw-max-h-[205px]">
              <span class="!tw-text-3xl !tw-font-medium">{{ value.description.charAt(0) }}</span
              >{{ value.description.slice(1) }}
            </p>
          </div>
        </div>
      </div>
    </MotionGroup>
  </section>

  <section
    v-if="variants.v3 === variant"
    class="n-section-secondary tw-overflow-hidden"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <!-- Watermark -->
    <BlockSymbol
      v-if="vars.watermarkImage"
      :block-logo-image="String(vars.watermarkImage)"
      :block-opacity="Number(vars?.watermarkOpacityNumber || 1)"
      position-text="left_bottom"
      :company-key="company.company_key"
    />
    <BlockSymbol
      v-if="vars.watermarkImage"
      :block-logo-image="String(vars.watermarkImage)"
      :block-opacity="Number(vars?.watermarkOpacityNumber || 1)"
      position-text="right_top"
      :company-key="company.company_key"
    />

    <!-- Title -->
    <div class="tw-mb-10 tw-flex tw-flex-col tw-gap-3 tw-self-center tw-text-center !tw-text-white lg:tw-mb-32">
      <small v-if="vars.altTitleText">{{ vars.altTitleText }}</small>
      <h2>{{ vars.titleText }}</h2>
    </div>

    <!-- Animated group -->
    <MotionGroup :preset="vars.animationText ? vars.animationText : 'fadeVisible'" :duration="600" is="div">
      <div
        class="tw-relative tw-z-10 tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-10 lg:tw-flex-row"
      >
        <!-- Mobile: swiper slider -->
        <div class="tw-w-full lg:tw-hidden">
          <div class="content-container">
            <div class="swiper-wrapper">
              <Swiper
                :modules="[SwiperPagination]"
                :clickable="true"
                :loop="true"
                :watch-overflow="true"
                @slideChange="onSlideChange"
                :breakpoints="{
                  320: { slidesPerView: 1 },
                  768: { slidesPerView: 1 },
                }"
                :spaceBetween="20"
                @swiper="onSwiper"
                class="tw-w-full"
              >
                <SwiperSlide v-for="(value, index) in values" :key="`value-slide-${index}`">
                  <div
                    class="value tw-group tw-mx-auto tw-flex tw-max-w-[600px] tw-flex-col tw-items-center tw-justify-start tw-gap-3 tw-rounded-[var(--rounded)] tw-px-4 tw-text-center tw-text-white"
                    style="min-height: 300px"
                  >
                    <NuxtImg
                      :src="value.icon"
                      class="tw-mb-3 tw-w-24 tw-transition-all tw-duration-500 group-hover:tw-translate-y-1"
                    />
                    <div>
                      <h6
                        class="tw-mb-4 tw-line-clamp-2 tw-transition-all tw-duration-500 group-hover:tw-text-[var(--c-primary)]"
                      >
                        {{ value.title }}
                      </h6>
                      <p
                        class="tw-mt-2 tw-overflow-auto"
                        :style="`color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'} !important`"
                      >
                        {{ value.description }}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
          </div>
          <div class="tw-mt-4">
            <LibrarySliderPagination
              :swiper="swiper"
              variant="v12"
              :can-paginate-back="canPaginateBack"
              :can-paginate-next="canPaginateNext"
            />
          </div>
        </div>

        <!-- Desktop & tablet: grid -->
        <div class="tw-hidden tw-w-full lg:tw-block">
          <!-- 3 cards: 1 row, 3 columns -->
          <template v-if="values.length === 3">
            <div class="tw-z-10 tw-mx-auto tw-grid tw-max-w-[1500px] tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-3">
              <div
                v-for="(value, index) in values"
                :key="index"
                class="value tw-group tw-flex tw-flex-col tw-items-center tw-justify-start tw-gap-6 tw-rounded-[var(--rounded)] tw-text-center tw-text-white lg:tw-h-[460px] xl:tw-h-[400px]"
              >
                <NuxtImg
                  :src="value.icon"
                  class="tw-mb-4 tw-w-24 tw-transition-all tw-duration-500 group-hover:tw-translate-y-1"
                />
                <div class="tw-mx-auto tw-max-w-[600px]">
                  <h3
                    class="tw-mb-2 tw-line-clamp-2 tw-transition-all tw-duration-500 group-hover:tw-text-[var(--c-primary)]"
                  >
                    {{ value.title }}
                  </h3>
                  <p
                    class="tw-overflow-auto"
                    :style="`color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'} !important`"
                  >
                    {{ value.description }}
                  </p>
                </div>
              </div>
            </div>
          </template>
          <!-- 4+ cards: 2×2 grid -->
          <template v-else>
            <div
              class="tw-z-10 tw-mx-auto tw-grid tw-max-w-[1500px] tw-grid-cols-1 tw-gap-x-6 tw-gap-y-4 lg:tw-grid-cols-2 lg:tw-gap-x-32"
            >
              <div
                v-for="(value, index) in values"
                :key="index"
                class="value tw-group tw-flex tw-flex-col tw-items-center tw-justify-start tw-gap-6 tw-rounded-[var(--rounded)] tw-text-center tw-text-white lg:tw-h-[460px] xl:tw-h-[400px]"
              >
                <NuxtImg
                  :src="value.icon"
                  class="tw-mb-4 tw-w-24 tw-transition-all tw-duration-500 group-hover:tw-translate-y-1"
                />
                <div class="tw-mx-auto tw-max-w-[600px]">
                  <h3
                    class="tw-mb-2 tw-line-clamp-2 tw-transition-all tw-duration-500 group-hover:tw-text-[var(--c-primary)]"
                  >
                    {{ value.title }}
                  </h3>
                  <p
                    class="tw-overflow-auto"
                    :style="`color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'} !important`"
                  >
                    {{ value.description }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </MotionGroup>
  </section>

  <section
    v-if="variants.v4 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <BlockSymbol
      v-if="vars.watermarkImage"
      :block-logo-image="String(vars.watermarkImage)"
      :block-opacity="Number(vars?.watermarkOpacityNumber || 1)"
      position-text="right_bottom"
      :company-key="company.company_key"
    />
    <BlockSymbol
      v-if="vars.watermarkImage"
      block-logo-image="https://files.pendlehillproperties.neuronwebsites.co.uk/pendlelefttop-kdeb77z57.png"
      :block-opacity="Number(vars?.watermarkOpacityNumber || 1)"
      position-text="left_top"
      :company-key="company.company_key"
    />
    <div class="tw-mb-10 tw-flex tw-flex-col tw-gap-3 tw-self-center tw-text-center lg:tw-mb-32">
      <h6 v-if="vars.altTitleText" class="tw-text-white">{{ vars.altTitleText }}</h6>
      <h2 class="tw-text-white">{{ vars.titleText }}</h2>
    </div>
    <MotionGroup :preset="vars.animationText ? vars.animationText : 'fadeVisible'" :duration="600" is="div">
      <div
        class="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-10 lg:tw-flex-row"
      >
        <div
          v-for="(value, index) in values"
          :key="index"
          class="value tw-group tw-flex tw-max-w-[600px] tw-flex-col tw-items-center tw-justify-start tw-gap-3 tw-rounded-[var(--rounded)] tw-text-center lg:tw-h-[460px] lg:tw-basis-1/3 lg:tw-items-start lg:tw-text-start xl:tw-h-[400px]"
        >
          <NuxtImg
            :src="value.icon"
            class="tw-mb-3 tw-w-24 tw-transition-all tw-duration-500 group-hover:tw-translate-y-1"
          />
          <div>
            <h3
              class="tw-line-clamp-2 tw-h-[55px] tw-text-white tw-transition-all tw-duration-500 group-hover:tw-text-[var(--c-primary)] md:tw-h-[65px]"
            >
              {{ value.title }}
            </h3>
            <p
              class="tw-mt-2 tw-max-h-[260px] tw-overflow-auto xl:tw-max-h-[205px]"
              :style="`color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'} !important`"
            >
              {{ value.description }}
            </p>
          </div>
        </div>
      </div>
    </MotionGroup>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import BlockSymbol from '~/components/common/BlockSymbol.vue';
import { Swiper as SwiperClass, SwiperSlide } from 'swiper/vue';
import { Pagination as SwiperPagination } from 'swiper';

export default defineNuxtComponent({
  name: 'OurValues',
  mixins: [ComponentMixin],
  components: {
    BlockSymbol,
    Swiper: SwiperClass,
    SwiperSlide,
  },
  data() {
    return {
      swiper: null as any,
      canPaginateBack: true,
      canPaginateNext: true,
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
        v4: 'v4',
      },
    };
  },
  computed: {
    values() {
      return this.groupedVariables.values;
    },
  },
  methods: {
    onSwiper(swiperInstance: any) {
      this.swiper = swiperInstance;
      this.onSlideChange();
    },
    onSlideChange() {
      if (!this.swiper) return;
      const isAtStart = this.swiper.isBeginning;
      const isAtEnd = this.swiper.isEnd;
      if (isAtStart) this.canPaginateBack = false;
      else this.canPaginateBack = true;
      if (isAtEnd) this.canPaginateNext = false;
      else this.canPaginateNext = true;
    },
  },
  mounted() {
    this.onSlideChange();
  },
});
</script>

<style scoped>
::-webkit-scrollbar {
  width: 2px;
}

::-webkit-scrollbar-track {
  background: var(--c-primary);
}

/* Handle */
::-webkit-scrollbar-thumb {
  -webkit-border-radius: 10px;
  border-radius: 10px;
  background: var(--c-tertiary);
}
::-webkit-scrollbar-thumb:window-inactive {
  display: none;
}
</style>
